import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, MessageSquare } from 'lucide-react';

interface NominationResponse {
  id: string;
  nomination_id: string;
  responder_user_id: string;
  responder_user_type: string;
  response_type: string;
  custom_message: string | null;
  admin_approval_status: string;
  admin_rejection_reason: string | null;
  created_at: string;
}

export default function NominationResponseReview() {
  const [responses, setResponses] = useState<NominationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReasons, setRejectionReasons] = useState<Record<string, string>>({});

  useEffect(() => {
    loadResponses();
  }, []);

  const loadResponses = async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from('nomination_responses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResponses(data || []);
    } catch (err) {
      console.error('Error loading responses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (response: NominationResponse) => {
    try {
      const { error } = await (supabase as any)
        .from('nomination_responses')
        .update({
          admin_approval_status: 'approved',
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', response.id);

      if (error) throw error;

      // Get the nomination to find the nominator
      const { data: nomination } = await (supabase as any)
        .from('player_nominations')
        .select('*')
        .eq('id', response.nomination_id)
        .maybeSingle();

      if (nomination?.nominator_user_id) {
        // Send notification to the nominator
        await supabase.from('notifications').insert({
          user_id: nomination.nominator_user_id,
          user_type: nomination.nominator_user_type || 'internal',
          to_user_id: nomination.nominator_user_id,
          notification_type: 'nomination_thank_you',
          category: 'nomination',
          title: '💝 Thank You for Your Nomination!',
          message: response.custom_message || `${nomination.player_name} says: "Thank You for Nominating Me, I really appreciate it!"`,
          priority: 'medium',
          requires_action: false,
        });
      }

      // Notify the responder that their message was approved
      await supabase.from('notifications').insert({
        user_id: response.responder_user_id,
        user_type: response.responder_user_type,
        to_user_id: response.responder_user_id,
        notification_type: 'nomination_response_approved',
        category: 'nomination',
        title: '✅ Your Thank You Message Was Approved',
        message: 'Your thank you message has been delivered to the person who nominated you!',
        priority: 'medium',
        requires_action: false,
      });

      toast.success('Response approved and notifications sent!');
      loadResponses();
    } catch (err) {
      console.error('Error approving response:', err);
      toast.error('Failed to approve response');
    }
  };

  const handleReject = async (response: NominationResponse) => {
    const reason = rejectionReasons[response.id];
    if (!reason?.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const { error } = await (supabase as any)
        .from('nomination_responses')
        .update({
          admin_approval_status: 'rejected',
          admin_rejection_reason: reason,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', response.id);

      if (error) throw error;

      // Notify the responder of rejection with reason
      await supabase.from('notifications').insert({
        user_id: response.responder_user_id,
        user_type: response.responder_user_type,
        to_user_id: response.responder_user_id,
        notification_type: 'nomination_response_rejected',
        category: 'nomination',
        title: '❌ Your Thank You Message Was Not Approved',
        message: `Your custom thank you message was not approved. Reason: ${reason}. You can still send the default thank you message.`,
        priority: 'medium',
        requires_action: false,
      });

      toast.success('Response rejected and user notified');
      loadResponses();
    } catch (err) {
      console.error('Error rejecting response:', err);
      toast.error('Failed to reject response');
    }
  };

  const pendingResponses = responses.filter(r => r.admin_approval_status === 'pending' && r.response_type === 'custom');
  const processedResponses = responses.filter(r => r.admin_approval_status !== 'pending' || r.response_type !== 'custom');

  if (loading) {
    return <Card><CardContent className="py-8 text-center text-muted-foreground">Loading responses...</CardContent></Card>;
  }

  return (
    <div className="space-y-6">
      {/* Pending Custom Responses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-500" />
            Pending Custom Thank You Messages ({pendingResponses.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingResponses.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No pending custom messages to review.</p>
          ) : (
            pendingResponses.map(response => (
              <div key={response.id} className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    From user: {response.responder_user_id.substring(0, 12)}... ({response.responder_user_type})
                  </span>
                  <span className="text-xs text-muted-foreground">{new Date(response.created_at).toLocaleString()}</span>
                </div>
                
                <div className="bg-muted/50 p-3 rounded-md">
                  <p className="text-sm font-medium text-foreground mb-1">Custom Message:</p>
                  <p className="text-sm text-muted-foreground">{response.custom_message}</p>
                </div>

                <Textarea
                  placeholder="Rejection reason (required for rejection)..."
                  value={rejectionReasons[response.id] || ''}
                  onChange={e => setRejectionReasons(prev => ({ ...prev, [response.id]: e.target.value }))}
                  className="text-sm"
                />
                
                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(response)}>
                    <CheckCircle className="h-4 w-4 mr-1" /> Approve & Send
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleReject(response)}>
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Processed Responses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            All Responses ({processedResponses.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {processedResponses.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No processed responses yet.</p>
          ) : (
            processedResponses.map(response => (
              <div key={response.id} className="border border-border rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm text-foreground">
                    {response.response_type === 'default_thank_you' ? 'Default Thank You' : 'Custom Message'}
                  </p>
                  {response.custom_message && (
                    <p className="text-xs text-muted-foreground mt-1">"{response.custom_message.substring(0, 80)}..."</p>
                  )}
                  <p className="text-xs text-muted-foreground">{new Date(response.created_at).toLocaleString()}</p>
                </div>
                <Badge className={response.admin_approval_status === 'approved' ? 'bg-green-600' : response.admin_approval_status === 'rejected' ? 'bg-destructive' : ''}>
                  {response.admin_approval_status}
                </Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
